import React, { useState } from 'react';
import { Card, Modal, PillButton, Row, Stack, Text, Title } from 'library/ui';
import { client, consoleLogInDev, useClient } from 'shared';
import { ShiftProviderUser } from 'models';
import RevealModal from '../ShiftCard/RevealModal';

export default function ComplianceActionInfoModal({
    modalOpen,
    setModalOpen,
    actionText,
    provider_user,
    business_location_id,
    refresh,
}: {
    modalOpen: boolean;
    setModalOpen: (open: boolean) => void;
    actionText: string;
    provider_user: ShiftProviderUser;
    business_location_id: number;
    refresh: () => void;
}) {
    const [revealOpen, setRevealOpen] = useState(false);
    const { loading, send } = useClient({
        url: 'api/compliance-action/initiate/',
        options: { sendImmediately: false },
    });
    const actionType = actionText.toLowerCase().replace(' ', '_');
    function onConfirm() {
        const body = {
            action: actionType,
            provider_user_id: provider_user?.id,
            business_location_id: business_location_id,
        };
        send({ method: 'POST', body })
            .then(() => {
                refresh();
            })
            .catch((e) => {
                alert('An error occurred. Please try again. If this persists please reach out to your ');
                consoleLogInDev(e);
            })
            .finally(() => setModalOpen(false));
    }

    const onSendCode = () => {
        try {
            setRevealOpen(true);
            client(`api/business/reveal-code/`, { method: 'POST', body: { provider: provider_user?.id } });
        } catch (error) {
            alert('Cannot send SSN reveal code');
        }
    };

    const ResubmissionContent = () => {
        return (
            <Stack gap="md">
                <Title size="h2">Action Required: Complete Resubmission</Title>
                <Text>
                    The worker you&apos;ve booked has successfully passed their background check and met all other state
                    requirements. However, due to a lapse of over 90 days since their last employment at a child care
                    facility, a resubmission in Florida&apos;s Clearinghouse is necessary before they can start.
                </Text>
                <Text weight="bold">
                    Please follow these steps promptly, as resubmissions can take a few days to process:
                </Text>
                <Card tone="warning">
                    <Stack gap="none">
                        <Text weight="bold">1. Search for the Worker in Clearinghouse:</Text>
                        <Row gap="sm">
                            <Text>- Visit the</Text>
                            <a href="https://crw.flclearinghouse.com/" target="_blank" rel="noreferrer">
                                Clearinghouse Search Page
                            </a>
                        </Row>
                    </Stack>
                    <Stack gap="sm">
                        <Text weight="bold">2. Pull up the worker&apos;s information to search for them</Text>
                        <Row justify="end" fullWidth>
                            <PillButton variant="secondary" onClick={() => onSendCode()}>
                                Reveal Information
                            </PillButton>
                        </Row>
                    </Stack>
                    <Stack gap="none">
                        <Text weight="bold">3. Initiate Resubmission:</Text>
                        <Text>
                            - Once you&apos;ve found the worker&apos;s profile, select &apos;Initiate Resubmission&apos;
                            and use information above to complete the process.
                        </Text>
                    </Stack>
                    <Text>
                        Note: The resubmission fee is typically between $13-$14. Tandem will reimburse this amount on
                        your next invoice.
                    </Text>
                </Card>
                <Text weight="bold">Reimbursement:</Text>
                <Text>
                    Tandem will credit your account for the resubmission fee on your next invoice. Please forward your
                    receipt from Clearinghouse to team@join-tandem.com.
                </Text>
                <Row justify="end" fullWidth>
                    <PillButton disabled={loading} onClick={onConfirm}>
                        I&apos;ve completed this
                    </PillButton>
                </Row>
            </Stack>
        );
    };

    const AgencyReviewContent = () => {
        return (
            <Stack gap="md">
                <Title size="h2">Action Required: Initiate Agency Review</Title>
                <Text>
                    The worker you&apos;ve booked has successfully passed their background check and met all other state
                    requirements. However, they can&apos;t start until you complete one final step in the Clearinghouse.
                </Text>
                <Text weight="bold">Please follow these steps to initiate the agency review:</Text>
                <Card tone="warning">
                    <Stack gap="none">
                        <Text weight="bold">1. Search for the Worker in Clearinghouse:</Text>
                        <Row gap="sm">
                            <Text>- Visit the</Text>
                            <a href="https://crw.flclearinghouse.com/" target="_blank" rel="noreferrer">
                                Clearinghouse Search Page
                            </a>
                        </Row>
                    </Stack>
                    <Stack gap="sm">
                        <Text weight="bold">2. Pull up the worker&apos;s information to search for them</Text>
                        <Row justify="end" fullWidth>
                            <PillButton variant="secondary" onClick={() => onSendCode()}>
                                Reveal Information
                            </PillButton>
                        </Row>
                    </Stack>
                    <Stack gap="none">
                        <Text weight="bold">3. Initiate Agency Review:</Text>
                        <Text>
                            - Once you&apos;ve found the worker&apos;s profile, select &apos;Initiate Agency
                            Review&apos; and use information above to complete the process.
                        </Text>
                    </Stack>
                </Card>
                <Row justify="end" fullWidth>
                    <PillButton disabled={loading} onClick={onConfirm}>
                        I&apos;ve completed this
                    </PillButton>
                </Row>
            </Stack>
        );
    };

    return (
        <>
            {modalOpen && (
                <Modal onClose={() => setModalOpen(false)} width={760}>
                    {actionText.toLowerCase().includes('resubmission') ? (
                        <ResubmissionContent />
                    ) : (
                        <AgencyReviewContent />
                    )}
                </Modal>
            )}
            <RevealModal open={revealOpen} onClose={() => setRevealOpen(false)} provider={provider_user} />
        </>
    );
}
